import { createSelector } from 'reselect';
import { appointmentDraftSelectors } from '~/api/appointment/appointment-draft.selectors';
import { dateUtils } from '../utils';
import {
  eligibleSlotsFilter as defaultEligibleSlotsFilter,
  getGroupsConfig,
} from '~/components/calendar-with-filters/date-filters';
import { groupSlotsByStartTime } from '~/components/calendar-with-filters/time-slots/helpers';
import { filterSlots } from '~/components/calendar-with-filters/helpers';
import { envConfigSelectors } from '~/components';

export const selectSlots = (state) => state.selectDate.slots;

export const selectFilters = (state) => state.selectDate.filters;

export const selectSlotsByAppointmentTypeId = (appointmentTypeId) =>
  createSelector(selectSlots, (slots) =>
    slots.map((slot) => ({
      ...slot,
      dates: [
        ...slot.dates.map((date) => ({
          ...date,
          times: date.times.filter((time) => time.appointmentTypeId === appointmentTypeId),
        })),
      ],
    })),
  );

export const selectFilteredTimeSlots = (appointmentTypeId) =>
  createSelector(
    selectSlotsByAppointmentTypeId(appointmentTypeId),
    selectFilters,
    envConfigSelectors.selectConfig(appointmentTypeId),
    (slots, selectedFilters, serviceConfig) =>
      filterSlots(
        slots,
        selectedFilters,
        serviceConfig?.Calendar?.eligibleSlotsFilter || defaultEligibleSlotsFilter,
      ),
  );

export const selectDateSlot = (appointmentTypeId) =>
  createSelector(
    appointmentDraftSelectors.selectAppointmentDateSlot,
    selectFilteredTimeSlots(appointmentTypeId),
    (selectedDateSlot, filteredSlots) => {
      const selectedMonth = filteredSlots.find(
        (filteredSlot) =>
          filteredSlot.month === dateUtils.getDateFromString(selectedDateSlot).getMonth(),
      );
      return (
        selectedMonth &&
        selectedMonth.dates.find((date) => date.date === selectedDateSlot && date.times.length > 0)
      );
    },
  );

export const selectFilteredTimeSlotsBySelectedDate = (appointmentTypeId) =>
  createSelector(
    selectDateSlot(appointmentTypeId),
    (selectedDate) => (selectedDate && selectedDate.times) || [],
  );

export const selectGroupedTimeSlotsBySelectedDate = (appointmentTypeId) =>
  createSelector(selectFilteredTimeSlotsBySelectedDate(appointmentTypeId), (slotTimes) =>
    groupSlotsByStartTime(slotTimes, getGroupsConfig()),
  );

export const selectTimeSlotsGroupTitleByAppointmentTypeId = (groupedSlotTimes, appointmentTypeId) =>
  createSelector(
    appointmentDraftSelectors.selectSelectedTimeSlotById(appointmentTypeId),
    (selectedTimeSlot) => {
      if (selectedTimeSlot.from) {
        return Object.entries(getGroupsConfig().periods).find(
          ([, period]) => selectedTimeSlot.from >= period.from && selectedTimeSlot.from < period.to,
        )[0];
      }
      const firstGroupWithSlots = Object.entries(groupedSlotTimes).find(
        ([, slotsGroup]) => slotsGroup.filter((slot) => !slot.disabled).length,
      );

      return (firstGroupWithSlots && firstGroupWithSlots[0]) || '';
    },
  );

export const selectDateSelectors = {
  selectSlotsByAppointmentTypeId,
  selectFilteredTimeSlotsBySelectedDate,
  selectTimeSlotsGroupTitleByAppointmentTypeId,
  selectFilteredTimeSlots,
  selectDateSlot,
  selectFilters,
};
